import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Hidden from "@insite/mobius/Hidden";
import LazyImage from "@insite/mobius/LazyImage";
import Link from "@insite/mobius/Link";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { COLORS, TYPOGRAPHY } from "@tSurya/theme/design";

import parse from "html-react-parser";
import React, { FC } from "react";
import { css } from "styled-components";

const enum fields {
    departments = "departments",
    img = "img",
    description = "description",
    imageAligment = "imageAligment",
    label = "label",
    link = "link",
    openInNewWindow = "openInNewWindow",
    backgroundColor = "backgroundColor",
    borderColor = "borderColor",
    textColor = "textColor",
}

interface MemberList {
    fields: {
        tittle: string;
        description: string;
        img: string;
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        members: any;
        [fields.departments]: MemberList[];
        [fields.img]: string;
        [fields.label]: string;
        [fields.link]: LinkFieldValue;
        [fields.description]: string;
        [fields.openInNewWindow]: boolean;
        [fields.backgroundColor]: string;
        [fields.borderColor]: string;
        [fields.textColor]: string;
        [fields.imageAligment]: "left" | "right";
    };
}

export interface SurCollaborationsDetailInfoStyles {
    tittle: InjectableCss;

    description: InjectableCss;
    box?: InjectableCss;
    container?: InjectableCss;
    icon?: InjectableCss;
    headingContainer?: InjectableCss;
    mainContainer?: InjectableCss;
    ContentImg?: InjectableCss;
    containerWrapper?: InjectableCss;
    imageWrapper?: InjectableCss;
    mobileContainer?: InjectableCss;
    mobileHeading?: InjectableCss;
    mobileDescription?: InjectableCss;
    descriptionContainer?: InjectableCss;
}

export const surCollaborationsDetailInfoStyles: SurCollaborationsDetailInfoStyles = {
    tittle: {
        css: css`
            font-family: "Mulish";
            font-style: normal;
            font-weight: 700;
            font-size: 16px;
            line-height: 24px;
            color: ${getColor("text.main")};
            flex: 1 1 50%;
            max-width: 156px;
        `,
    },
    description: {
        css: css`
            font-family: "Mulish";
            font-style: normal;
            font-weight: 400;
            font-size: 14px;
            line-height: 22px;
            color: #545454;
            margin-bottom: 25px;
        `,
    },
    box: {
        css: css`
            display: flex;
            gap: 20px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-bottom: 15px;
                    `,
                    null,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    icon: {
        css: css`
            max-width: 24px;
            max-height: 25px;
        `,
    },
    headingContainer: {
        css: css`
            margin-top: 86px;
            margin-bottom: 58px;
        `,
    },
    mainContainer: {
        css: css`
            border: 1px solid blue;
            max-width: 611px;
            margin: 0 auto;
            /* padding: 0px 41px; */
        `,
    },
    ContentImg: {
        css: css`
            width: 100%;
            max-width: 308px;
            img {
                object-fit: contain;
                width: 100%;
                max-width: 308px;
                /* min-width: 420px; */
                aspect-ratio: 1/1;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        max-width: 100%;
                    `,
                    css`
                        max-width: 100%;
                    `,
                    css`
                        max-width: 100%;
                    `,

                    null,
                    null,
                ])}
        `,
    },
    imageWrapper: {
        css: css`
            width: 100%;
            height: 100%;
            /* flex: 0 0 49%; */
            max-width: 308px;
        `,
    },
    containerWrapper: {
        css: css`
            padding-bottom: 80px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding-top: 26px;
                        padding-bottom: 40px;
                    `,
                    css`
                        padding-top: 26px;
                        padding-bottom: 40px;
                    `,
                    css`
                        padding-top: 26px;
                        padding-bottom: 40px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    mobileContainer: {
        css: css`
            padding: 0 20px;
            /* background: rgba(241, 239, 234, 0.5); */
            background: rgba(248, 248, 248, 0.5);
            padding: 30px;
        `,
    },
    mobileHeading: {
        css: css`
            margin-top: 20px;
            margin-bottom: 20px;
            font-family: "Mulish";
            font-style: normal;
            font-weight: 600;
            font-size: 28px;
            line-height: 38px;
            text-align: center;
            text-transform: uppercase;
            color: ${getColor("text.main")};
        `,
    },
    mobileDescription: {
        css: css`
            margin-top: 10px;
        `,
    },
    descriptionContainer: {
        css: css`
            /* border: 1px solid red; */
            max-width: 560px;
            margin-top: 54px;
        `,
    },
};

const styles = surCollaborationsDetailInfoStyles;

export const SurCollaborationsDetailInfo: FC<OwnProps> = ({ fields }) => {
    const { url } = useGetLink(fields.link);
    const containerStyles = {
        css: css`
            display: flex;
            max-width: 990px;
            margin: 0 auto;
            padding: 40px;
            background: rgba(241, 239, 234, 0.5);
            gap: 42px;
            flex-direction: ${fields.imageAligment};
        `,
    };
    const ctaLink = {
        css: css`
            outline: 0;
            background-color: ${fields.backgroundColor};
            border: 1px solid ${fields.borderColor};
            padding: 10px 34px;
            width: max-content;
            max-width: 100%;
            margin: 0 auto;
            span[class^="TypographyStyle"] {
                color: ${getColor("text.main")};
                &:hover {
                    color: #545454;
                }
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: 10px 22px;
                    `,
                    css`
                        padding: 10px 22px;
                    `,
                    css`
                        padding: 10px 22px;
                    `,
                    null,
                    null,
                ])}
            span {
                display: block;
                font-family: "Mulish";
                font-style: normal;
                font-weight: 700;
                font-size: 16px;
                line-height: 24px;
                text-align: center;
                color: ${fields.textColor} !important;
                font-style: normal !important;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-top: 10px;
                        display: flex;
                        padding: 7px 34px;
                        span {
                            font-size: 14px;
                            line-height: 18px;
                        }
                    `,
                    css`
                        margin-top: 10px;
                        display: flex;
                        padding: 7px 34px;
                        span {
                            font-size: 14px;
                            line-height: 18px;
                        }
                    `,
                    css`
                        margin-top: 10px;
                        display: flex;
                        padding: 7px 34px;
                        span {
                            font-size: 14px;
                            line-height: 18px;
                        }
                    `,
                    null,
                    null,
                ])}
        `,
    };

    return (
        <>
            <StyledWrapper {...styles.containerWrapper}>
                <Hidden below="lg">
                    <StyledWrapper {...containerStyles}>
                        {fields.img && (
                            <StyledWrapper {...styles.imageWrapper}>
                                <LazyImage
                                    src={fields.img}
                                    {...styles.ContentImg}
                                    altText="Collabration Detail Image"
                                />
                            </StyledWrapper>
                        )}
                        <StyledWrapper {...styles.descriptionContainer}>
                            {fields.description && (
                                <StyledWrapper {...styles.description}>
                                    {parse(fields.description, parserOptions)}
                                </StyledWrapper>
                            )}
                            {fields.label && fields.link.value ? (
                                <Link
                                    href={url}
                                    target={fields.openInNewWindow ? `_blank` : `_self`}
                                    aria-label={fields.label || "Collabration Link"}
                                    {...ctaLink}
                                >
                                    {fields.label}
                                </Link>
                            ) : null}
                        </StyledWrapper>
                    </StyledWrapper>
                </Hidden>
                <Hidden above="md">
                    <StyledWrapper {...styles.mobileContainer}>
                        <LazyImage src={fields.img} {...styles.ContentImg} altText="Collabration Detail Image" />
                        <StyledWrapper {...styles.mobileDescription}>
                            {parse(fields.description, parserOptions)}
                        </StyledWrapper>
                        {fields.label && fields.link.value ? (
                            <Link
                                href={url}
                                target={fields.openInNewWindow ? `_blank` : `_self`}
                                aria-label={fields.label || "Collabration Link"}
                                {...ctaLink}
                            >
                                {fields.label}
                            </Link>
                        ) : null}
                    </StyledWrapper>
                </Hidden>
            </StyledWrapper>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: SurCollaborationsDetailInfo,
    definition: {
        group: "Basic",
        displayName: "SurCollaborationsDetailInfo",
        fieldDefinitions: [
            {
                fieldType: "Translatable",
                name: fields.img,
                displayName: "Image",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
            },

            {
                fieldType: "Translatable",
                name: fields.description,
                editorTemplate: "RichTextField",
                displayName: "Decription",
                defaultValue: "",
            },
            {
                name: fields.imageAligment,
                fieldType: "General",
                displayName: "Image Aligment",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Left", value: "row" },
                    { displayName: "Right", value: "row-reverse" },
                ],
                defaultValue: "Left",
            },
            {
                name: fields.label,
                fieldType: "General",
                displayName: "Button Label",
                editorTemplate: "TextField",
                defaultValue: "",
            },

            {
                name: fields.backgroundColor,
                displayName: "Background Color",
                editorTemplate: "ColorPickerField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.borderColor,
                displayName: "Border Color",
                editorTemplate: "ColorPickerField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.textColor,
                displayName: "Text Color",
                editorTemplate: "ColorPickerField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.link,
                displayName: "Button Link",
                fieldType: "General",
                editorTemplate: "LinkField",
                defaultValue: {
                    value: "",
                    type: "Page",
                },
            },
            {
                name: fields.openInNewWindow,
                fieldType: "General",
                editorTemplate: "CheckboxField",
                defaultValue: false,
            },
        ],
    },
};
export default widgetModule;
